import dayjs, { ConfigType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);

export const DEFAULT_DISPLAY_DATE_FORMAT = 'MM/dd/yyyy';

/**
 * Takes a date string and returns a date object
 * @param dateString Date string formatted as MM/DD/YYY
 */
export const createDateObject = (dateString: string) => {
  const parts = dateString.split('/');
  if (parts[0] && parts[1] && parts[2]) {
    return new Date(+parts[2], +parts[0] - 1, +parts[1]);
  }
  throw new Error('Invalid date string + ' + dateString);
};

export const createISODateInUTC = (dateString: string) => {
  return dayjs(dateString).format('YYYY-MM-DDT00:00:00.000') + 'Z';
};

interface ISODateToDisplayConfig {
  format?: string;
}

/**
 * Takes an ISO date string and returns a display date string
 * formatted MM/DD/YYY
 * @param date ISO Date string
 */
export const isoDateToDisplay = (date: string, config?: ISODateToDisplayConfig) => {
  const format = config?.format || 'MM/DD/YYYY';
  return dayjs(new Date(date)).format(format);
};

export const isoDateToDisplayInUTC = (date: string) => {
  return dayjs(date).utc().format('MM/DD/YYYY');
};

/**
 * Formats a timestamp (sec) or date string
 * @param date Timestamp in sec or Date string
 */
export const formatDate = (date?: ConfigType, formattedDate?: string, useOnlyUTC = false): string => {
  if (!date) return '';

  let dateObj = typeof date === 'number' ? dayjs.unix(date) : dayjs(date);
  if (isNaN(dateObj.toDate().getTime())) return '';
  // adding this check to ignore DB default date (0001-01-01T00:00:00Z)
  if (dateObj.year() < 1900) return '';

  if (useOnlyUTC) dateObj = dateObj.utc();

  return dateObj.format(formattedDate);
};

/**
 *
 * @param date Timestamp in sec or Date string
 * @param formattedDate Desired output format, example: 'MM/DD/YYYY'
 */
export const formatBirthDate = (date?: ConfigType, formattedDate?: string): string => {
  // match yyyy-mm-dd and remove the rest.
  // To avoid timezone issues

  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}(.+)$/.test(date)) {
    const dateOnlyStr = date.match(/^(\d{4}-\d{2}-\d{2})/)?.[0];
    return dayjs(dateOnlyStr).format(formattedDate);
  }

  return formatDate(date, formattedDate);
};

export const getTodaysDate = (formattedDate: string) => dayjs().format(formattedDate);

export enum Unit {
  MILLISECOND = 'millisecond',
  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

type DurationFrom = {
  date?: string;
  duration: number;
  unit: Unit;
  format?: string;
};

export const getPastDate = ({ date, duration, format, unit }: DurationFrom) =>
  (date ? dayjs(date) : dayjs()).subtract(duration, unit).format(format ?? 'MM/DD/YYYY');

export const getFutureDate = ({ date, duration, format, unit }: DurationFrom) =>
  (date ? dayjs(date) : dayjs()).add(duration, unit).format(format ?? 'MM/DD/YYYY');

export const isDateValid = (date: string | Date) => dayjs(date).isValid();

export const formatDateInMonthAndDate = (date: Date): string => {
  if (!date) return '';
  const monthNameWithDate = dayjs(date).format('MMMM Do');
  return monthNameWithDate;
};

export const formatDateInMonthAndDateWithYear = (date: Date): string => {
  if (!date) return '';
  const monthNameWithDateAndYear = dayjs(date).format('MMMM D, YYYY');
  return monthNameWithDateAndYear;
};
/**
 * Returns 1, 0, or -1 if date1 is greater, equal, or less than date2
 * Useful for sort functions
 */
export const dateCompare = (date1: Date | string | undefined, date2: Date | string | undefined) => {
  return date1 && date2 ? dayjs(date1).diff(dayjs(date2)) : 0;
};
